import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { ErrorType } from '~/data/ErrorRecord';
import UserManagementAPI, {
  UsersResponse,
  InvitesResponse,
} from '~/api/user-management-api';
import { createMatcher } from '~/utils/create-action/create-matcher';
import type UserRoleConstants from '~/constants/user-role-constants';
import createCaughtAsyncThunk from '~/utils/create-action/create-caught-async-thunk';
import { defaultState } from './data/UserManagementState';

export const getUsers = createCaughtAsyncThunk<
  UsersResponse,
  { role?: UserRoleConstants }
>('getUsers', async (args) => {
  if (args.role) {
    return UserManagementAPI.getUsersWithRole(args.role);
  } else {
    return UserManagementAPI.getAllUsers();
  }
});

export const getInvites = createCaughtAsyncThunk<InvitesResponse>(
  'getInvites',
  async () => {
    return UserManagementAPI.getInvites();
  }
);

const slice = createSlice({
  name: 'users-management',
  initialState: defaultState,
  reducers: {
    forceReload: (state) => {
      state.forceLoad = true;
    },
    resetInvitationSent: (
      state,
      action: PayloadAction<{ inviteId: string }>
    ) => {
      const inviteIndex = state.inviteList.invites.findIndex(
        (i) => i.inviteId === action.payload.inviteId
      );
      if (inviteIndex === -1) {
        return;
      }

      delete state.inviteList.invites[inviteIndex].resendInvitation;
    },
    toggleUserNotFullyFetchedModalOpen: (state) => {
      state.isUserFullyNotFetchedModalOpen = !state.isUserFullyNotFetchedModalOpen;
    },
    setInvitationResent: (
      state,
      action: PayloadAction<{
        inviteId: string;
        isLoading: boolean;
        error?: ErrorType;
      }>
    ) => {
      const inviteIndex = state.inviteList.invites.findIndex(
        (i) => i.inviteId === action.payload.inviteId
      );
      if (inviteIndex === -1) {
        return;
      }

      state.inviteList.invites[inviteIndex].resendInvitation = {
        isLoading: action.payload.isLoading,
        error: action.payload.error,
      };
    },
    setForceReload: (state, action: PayloadAction<{ forceLoad: boolean }>) => {
      state.forceLoad = action.payload.forceLoad;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
        state.forceLoad = false;
        state.userList.users = [];
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.userList.users = action.payload.users;
      });

    builder
      .addCase(getInvites.pending, (state) => {
        state.isLoading = true;
        state.forceLoad = false;
        state.inviteList.invites = [];
      })
      .addCase(getInvites.fulfilled, (state, action) => {
        state.isLoading = false;
        state.inviteList.invites = action.payload.invites;
      });

    builder.addMatcher(
      createMatcher([getInvites.rejected.type, getUsers.rejected.type]),
      (state, action) => {
        state.isLoading = false;
        if (action.meta.aborted) {
          return;
        }
        state.error = action.payload;
      }
    );
  },
});

export const {
  forceReload,
  resetInvitationSent,
  toggleUserNotFullyFetchedModalOpen,
  setInvitationResent,
  setForceReload,
} = slice.actions;
export default slice.reducer;
